import { CalculationTableParams, PrivateFilterType } from '../calculationsTypes';
import { CalculationWithRelatedEntitiesResponse, Operation, SortDirection } from '@ydistri/api-sdk';
import { CalculationSelect } from '../../../components/global/CalculationSelect/CalculationSelect';
import React, { ChangeEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { Column, Input, Row, Text } from '@ydistri/ds';
import { DragHandleBase } from '../../../components/global/DragHandleBase/DragHandleBase';
import { TbPlus } from 'react-icons/tb';
import DeleteIconButton from '../../../components/buttons/DeleteIconButton';
import { styled } from 'styled-components';

export interface ManualCalculationData {
  manualCalculationIds: number[];
  calculationTitles: Record<number, string | undefined>;
}

export interface ManualCalculationSelectProps {
  onChange: (value: ManualCalculationData) => void;
  defaultValues?: ManualCalculationData;
}

const manualSelectQueryParams: CalculationTableParams = {
  sortings: [
    {
      fieldName: 'Created',
      direction: SortDirection.Desc,
    },
  ],
  includePrivateCalculations: PrivateFilterType.NONE,
  conditions: [
    {
      fieldName: 'Mode',
      operation: Operation.Eq,
      value: 'Manual',
    },
  ],
};

const ManualRowStyled = styled(Row)`
  width: 60rem;
  max-width: 60rem;
  flex-wrap: nowrap;
  margin-bottom: 0.5rem;
`;

const ManualTitleWrapper = styled(Row)`
  width: 33.75rem;
`;

/**
 * Multi-select for manual calculations with renamable titles.
 */
const ManualCalculationSelect: React.FC<ManualCalculationSelectProps> = ({
  onChange,
  defaultValues,
}) => {
  const [manualCalculationIds, setManualCalculationIds] = useState<number[]>(
    () => defaultValues?.manualCalculationIds ?? [],
  );
  const [calculationTitles, setCalculationTitles] = useState<Record<number, string | undefined>>(
    () => defaultValues?.calculationTitles ?? {},
  );

  const onRemove = useCallback((calculationId: number) => {
    setManualCalculationIds(p => p.filter(id => id !== calculationId));
    setCalculationTitles(p => ({ ...p, [calculationId]: undefined }));
  }, []);

  const onChangeCalculationTitle = useCallback((calculationId: number, title: string) => {
    setCalculationTitles(p => ({ ...p, [calculationId]: title }));
  }, []);

  const onChangeInput = useCallback(
    (calculationId: number) => {
      const handler: ChangeEventHandler<HTMLInputElement> = e => {
        onChangeCalculationTitle(calculationId, e.target.value);
      };
      return handler;
    },
    [onChangeCalculationTitle],
  );

  const onCalculationSelected = useCallback(
    (calculation: CalculationWithRelatedEntitiesResponse | null) => {
      if (!calculation) return;
      setManualCalculationIds(p => (p.includes(calculation.id) ? p : [...p, calculation.id]));
      setCalculationTitles(p => ({ ...p, [calculation.id]: calculation.title }));
    },
    [],
  );

  useEffect(() => {
    onChange({ manualCalculationIds, calculationTitles });
  }, [manualCalculationIds, calculationTitles, onChange]);

  const addRow = useMemo(
    () => (
      <Row $flexWrap="nowrap" $gap="0.5rem">
        <DragHandleBase>
          <TbPlus size={20} />
        </DragHandleBase>
        <CalculationSelect
          selectedCalculationId={null}
          onChange={onCalculationSelected}
          queryParams={manualSelectQueryParams}
          placeholder="Select manual calculation to add"
        />
      </Row>
    ),
    [onCalculationSelected],
  );

  return (
    <Column $gap="0.5rem">
      <Column>
        {manualCalculationIds.map((calculationId, i) => (
          <ManualRowStyled key={calculationId} $alignItems="center" $gap="1rem">
            <DragHandleBase />
            <ManualTitleWrapper $gap="0.5rem" $alignItems="center" $flexWrap="nowrap">
              <Text>[{i + 1}]</Text>
              <Input
                size="large"
                value={calculationTitles[calculationId]}
                infoFlexGrow={1}
                onChange={onChangeInput(calculationId)}
                suffix={
                  <Text $type="light" $size="small">
                    calculation #{calculationId}
                  </Text>
                }
                $addPadding
              />
            </ManualTitleWrapper>
            <DeleteIconButton
              onClick={() => onRemove(calculationId)}
              tooltip="Remove manual calculation"
              iconType="x"
            />
          </ManualRowStyled>
        ))}
      </Column>
      {addRow}
    </Column>
  );
};

export default ManualCalculationSelect;
